import React from 'react';
import { unescape, isEmpty } from 'lodash';
import get from 'lodash/get';

import { I18n } from 'common/i18n';
import { NULL_GROUPING } from './Constants';
import { ValueRenderer as valueRenderer, type IValueRendererProps } from './ValueRenderer';
import { shouldRenderAsHtml } from './helpers/TableColumnFormatting';
import type { CustomAgGridContext } from 'common/types/agGrid/context';
import { isTotalCell } from './helpers/isTotalCell';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';

const scope = 'shared.visualizations.charts';

const translations = {
  noValue: I18n.t('common.summary_table.no_value', { scope }),
  total: I18n.t('table.total', { scope }),
  subtotal: I18n.t('table.subtotal', { scope }),
};

export const GroupAndTotalRenderer = (props: IValueRendererProps) => {
  // Since props.data can be either TData or undefined, we want to skip rendering
  // if the dataset is empty or if there are zero results, as DataTypeFormatter expects a result.
  // Sample props.data = {date: '2021-01-30T00:00:00.000', id: '1'}
  if (isEmpty(props.data) && isTotalCell(props)) {
    return null;
  }

  const { hierarchyConfig, groupedColumns = [] } = props.context as CustomAgGridContext;
  const totalLabel = translations.total;

  const footer = props.node.footer;
  const isRootLevel = props.node.level === -1;
  const columnMetadata = props.context.columnMetadata ?? [];
  // in multipleColumns mode, node.key is the same for all auto group columns in a row.
  // only show "No Value" on the column that matches the row's group level so it
  // doesn't duplicate across columns on both regular and subtotal rows.
  const columnGroupField =
    typeof props.colDef?.showRowGroup === 'string' ? props.colDef.showRowGroup : undefined;
  const columnLevel = columnGroupField ? groupedColumns.indexOf(columnGroupField) : -1;
  const isMatchingColumn = columnLevel === -1 || columnLevel === props.node.level;

  let value: string | React.ReactElement = '';

  if (footer && !isRootLevel && columnGroupField) {
    // auto group column on a subtotal row in multipleColumns mode:
    // valueRenderer can't resolve auto group column field names, so we
    // determine the value directly from node.key / props.value
    if (isMatchingColumn) {
      value =
        props.node.key === NULL_GROUPING ? translations.noValue : String(props.value ?? props.node.key ?? '');
    }
  } else if (props.node.key === NULL_GROUPING && isMatchingColumn) {
    value = translations.noValue;
  } else if (props.node.key === NULL_GROUPING) {
    // non-matching column on a null-group row, leave value as empty string
  } else {
    const renderedValue = valueRenderer(props);
    // If it's already a React component (like CustomLinkRenderer), use it directly
    if (React.isValidElement(renderedValue)) {
      value = renderedValue;
    } else {
      // We are unescaping the value returned by valueRenderer
      // because AgGrid auto group will escape the value we pass, and we do not want to double escape
      // except URL or Email
      value = shouldRenderAsHtml(props.node, columnMetadata) ? renderedValue : unescape(renderedValue);
    }
  }

  const customSubtotalLabel = get(hierarchyConfig, 'customLabels.subtotals');

  // Currently, we don't need to show a custom label when exporting XLSX.
  let subtotalLabel = translations.subtotal;

  if (footer) {
    if (isRootLevel) {
      return <span>{totalLabel}</span>;
    } else {
      if (isTableAdvancedTotalingEnabled() && !props.forExport) {
        // If there is a custom subtotal label inputted in the 'Tab Setup'
        if (!isEmpty(customSubtotalLabel)) {
          subtotalLabel = customSubtotalLabel;
        }

        const hierarchyColumnConfigs = props.context.hierarchyConfig?.columnConfigurations || [];
        const hierarchyRowGroupingColumns = hierarchyColumnConfigs.filter(
          (hierarchyColumnConfig) => hierarchyColumnConfig.isGrouping,
        );
        // If there is a custom subtotal label inputted in the 'Advanced Totaling Modal'
        if (hierarchyRowGroupingColumns[props.node.level]?.subtotalLabel) {
          subtotalLabel = hierarchyRowGroupingColumns[props.node.level].subtotalLabel;
        }
      }
      if (isEmpty(value)) {
        if (!props.forExport) {
          return null;
        } else {
          return '';
        }
      }
      return (
        <span data-testid="subtotal-cell">
          {subtotalLabel} {value}
        </span>
      );
    }
  } else {
    // If value is a React component (from ValueRenderer), return it directly
    if (React.isValidElement(value)) {
      return value;
    }
    return <span>{value}</span>;
  }
};
